import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Alert,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Stack,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import useTemplate from "../../hooks/useTemplate";
import JsonPrettyView from "../../widgets/JsonPrettyView";
import TemplatePreview from "../../widgets/TemplatePreview";
import MuPb from "../../widgets/MuPb";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import SchemaIcon from "@mui/icons-material/Schema";
import { MuiTelInput } from "mui-tel-input";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import BackBtn from "../../widgets/BackBtn";

const TemplateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoading, data, isError, error } = useTemplate(id);
  const [showJson, setShowJson] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [testMessageStatus, setTestMessageStatus] = useState({
    loading: false,
    error: null,
    success: null,
  });

  const handleSendTest = useCallback(async () => {
    if (!phoneNumber.trim()) return;

    setTestMessageStatus({ loading: true, error: null, success: null });

    try {
      const response = await axios.post(
        `${API_ENDPOINT}/api/send-test-msg-template`,
        {
          templateId: id,
          phone: phoneNumber.trim(),
        },
        getHeader()
      );
      setTestMessageStatus({
        loading: false,
        error: null,
        success: response?.data?.message || "Sent successfully!",
      });
    } catch (err) {
      setTestMessageStatus({
        loading: false,
        error: err?.response?.data?.message || "Failed to send",
        success: null,
      });
    }
  }, [id, phoneNumber]);

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{error?.message}</Alert>;
  if (!data) return <Alert severity="warning">No template found.</Alert>;

  return (
    <Container sx={{ my: 3 }}>
      <BackBtn />
      <Typography variant="h6" gutterBottom>
        <code>{data.name || "Template"}</code> Details
      </Typography>

      <Grid container spacing={3}>
        {/* Template Preview */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Template Preview"
              action={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowJson(true)}
                  sx={{ marginTop: "-4px", marginBottom: "-4px" }} // Adjust vertical alignment
                >
                  View JSON
                </Button>
              }
            />
            <CardContent>
              <TemplatePreview template={data} previewUrl={data.previewUrl} />
            </CardContent>
          </Card>
        </Grid>

        {/* Send Test Message */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Utilise template" />
            <CardContent>
              <Stack spacing={2}>
                <Box display={"flex"} justifyContent={"center"}>
                  <Button
                    sx={{ width: 200, py: 1 }}
                    variant="contained"
                    startIcon={<SchemaIcon />}
                  >
                    Build a flow
                  </Button>
                </Box>
                <Box display={"flex"} justifyContent={"center"}>
                  <Button
                    sx={{ width: 200, py: 1 }}
                    variant="contained"
                    onClick={() => {
                      navigate(`/home/${id}/template-campaign`);
                    }}
                    startIcon={<CampaignOutlinedIcon />}
                  >
                    Launch a Campaign
                  </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6">Send Test message</Typography>
                <MuiTelInput
                  defaultCountry="IN"
                  value={phoneNumber}
                  forceCallingCode
                  onChange={(newValue) => {
                    setPhoneNumber(newValue);
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendTest}
                  disabled={testMessageStatus.loading}
                >
                  {testMessageStatus.loading
                    ? "Sending..."
                    : "Send Test Message"}
                </Button>
                {testMessageStatus.error && (
                  <Alert severity="error">{testMessageStatus.error}</Alert>
                )}
                {testMessageStatus.success && (
                  <Alert severity="success">{testMessageStatus.success}</Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog
        open={showJson}
        onClose={() => setShowJson(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Template JSON</DialogTitle>
        <DialogContent>
          <JsonPrettyView prettifiedJson={data} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowJson(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplateDetails;
